import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Typography, Layout, Icon, Alert, Collapse, Radio, Tag, AutoComplete, Modal } from 'antd';
import { performLogin, saveOrganisation, getSavedOrganisations } from './utils/authService';
import { getServerType, setServer, getServerDisplayName, getServerDomain, getServer } from './utils/serverConfig';

const { Panel } = Collapse;

const { Title } = Typography;
const { Content } = Layout;

const LoginPage = ({ form, onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [serverType, setServerType] = useState(getServerType());
  const [savedOrganisations, setSavedOrganisations] = useState([]);

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('=== LoginPage Component Mounted ===');
    console.log('onLoginSuccess prop:', typeof onLoginSuccess);
    console.log('form prop:', form ? 'present' : 'missing');
    console.log('Current server:', getServerDisplayName());
    
    // Load saved organisations
    const saved = getSavedOrganisations();
    setSavedOrganisations(saved);
    console.log('Loaded saved organisations:', saved);
  }, []);

  const handleServerChange = (e) => {
    const newServerType = e.target.value;
    const currentType = serverType;
    
    // If clicking the already selected option, do nothing
    if (newServerType === currentType) return;
    
    const targetLabel = newServerType === 'production' ? 'Production' : 'Demo';
    const targetDomain = newServerType === 'production' ? 'theassetpro.com' : 'quonitrix.com';
    
    Modal.confirm({
      title: 'Switch server?',
      content: (
        <span>
          You are about to switch from <strong>{currentType === 'production' ? 'Production' : 'Demo'}</strong> to <strong>{targetLabel}</strong> ({targetDomain}).
          {localStorage.getItem('auth_token') && (
            <span style={{ display: 'block', marginTop: 8, color: '#faad14' }}>
              You will be logged out and need to sign in again.
            </span>
          )}
          <br /><br />
          Do you want to continue?
        </span>
      ),
      okText: 'Yes, switch',
      cancelText: 'Cancel',
      onOk: () => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          message.warning('Server changed. Please sign in again with the new server.', 3);
        }
        setServer(newServerType);
        setServerType(newServerType);
        message.info(`Switched to ${targetLabel} server (${targetDomain})`);
        console.log('Server changed to:', newServerType);
      },
      onCancel: () => {
        // Keep radio on current selection (no-op; Radio is controlled by serverType which we didn't change)
      },
    });
  };

  const handleTestClick = async () => {
    console.log('=== TEST API CALL BUTTON CLICKED ===');
    
    const token = localStorage.getItem('auth_token');
    if (!token) {
      message.error('Please login first to get a token');
      return;
    }

    const payload = {
      "asset": null,
      "location": "all",
      "subLocation": "all",
      "periodFrom": "2025-10-31T18:30:00.000Z",
      "periodTo": "2025-12-01T18:29:59.999Z"
    };

    // Use the selected server
    const apiBaseUrl = getServer();
    const apiUrl = `${apiBaseUrl}/fuel/mileage-report`;

    console.log('Making API call to:', apiUrl);
    console.log('Server type:', serverType);
    console.log('Payload:', payload);
    console.log('Token:', token.substring(0, 20) + '...');

    try {
      setLoading(true);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': 'newa',
          'X-Api-Version': '2.0',
        },
        body: JSON.stringify(payload),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.warn('Response is not JSON, received text:', text);
        data = { rawText: text, note: 'Response was not JSON' };
      }
      
      console.log('=== API RESPONSE ===');
      console.log('Full response:', data);
      console.log('Response type:', typeof data);
      console.log('Response keys:', data && typeof data === 'object' ? Object.keys(data) : 'N/A');
      
      // Display in alert and set debug info
      setDebugInfo({
        status: response.status,
        success: response.ok,
        server: serverType,
        url: apiUrl,
        payload: payload,
        response: data,
        responseSize: JSON.stringify(data).length,
        contentType: contentType
      });

      if (response.ok) {
        const responseStr = JSON.stringify(data);
        message.success(`API call successful! Response size: ${responseStr.length} bytes`);
        console.log('Response preview (first 500 chars):', responseStr.substring(0, 500));
      } else {
        message.error(`API call failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('API call error:', error);
      setDebugInfo({
        status: 'error',
        error: error.message,
        stack: error.stack
      });
      message.error(`API call failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const performLoginCall = async (values) => {
    console.log('=== LOGIN ATTEMPT STARTED ===');
    console.log('Form values:', { email: values.email, organisation: values.organisation, password: '***' });
    
    if (!values || !values.email || !values.password || !values.organisation) {
      console.error('Missing form values:', values);
      message.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setErrorMessage(null);
    setDebugInfo(null);
    
    try {
      const { email, password, organisation } = values;
      
      console.log('Calling performLogin...');
      const result = await performLogin(email, password, organisation);
      
      console.log('Login result:', { 
        success: result.success, 
        hasToken: !!result.token,
        hasUserData: !!result.userData,
        error: result.error 
      });
      
      if (result.success) {
        console.log('Login successful! User data:', result.userData);
        message.success('Login successful! Redirecting...', 2);
        
        // Save organisation for future use
        const organisationSlug = values.organisation;
        const organisationObj = result.userData.organisation;
        const organisationName = organisationObj && typeof organisationObj === 'object' 
          ? organisationObj.name 
          : (typeof organisationObj === 'string' ? organisationObj : organisationSlug);
        
        if (organisationSlug && organisationName) {
          saveOrganisation(organisationSlug, organisationName);
          // Update saved organisations list
          const updated = getSavedOrganisations();
          setSavedOrganisations(updated);
        }
        
        // Small delay to show success message
        setTimeout(() => {
          if (onLoginSuccess) {
            console.log('Calling onLoginSuccess callback');
            // Include organisation in userData if not already present
            const userDataWithOrg = {
              ...result.userData,
              organisation: result.userData.organisation || values.organisation
            };
            onLoginSuccess(userDataWithOrg);
          } else {
            console.warn('onLoginSuccess callback not provided');
          }
        }, 500);
      } else {
        const errorMsg = result.error || 'Login failed. Please check your credentials.';
        console.error('Login failed:', errorMsg);
        setErrorMessage(errorMsg);
        setDebugInfo({
          status: 'failed',
          error: errorMsg,
          details: result
        });
        message.error(errorMsg, 5);
      }
    } catch (error) {
      console.error('Login exception:', error);
      const errorMsg = error.message || 'An unexpected error occurred. Please try again.';
      setErrorMessage(errorMsg);
      setDebugInfo({
        status: 'exception',
        error: errorMsg,
        stack: error.stack
      });
      message.error(errorMsg, 5);
    } finally {
      setLoading(false);
      console.log('=== LOGIN ATTEMPT COMPLETED ===');
    }
  };

  return (
    <Layout className="aim-login-layout">
      <Content className="aim-login-content">
        <Card className="aim-login-card">
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Title level={2} className="aim-login-title">
              AssetPro Intelligence and Monitoring (AIM)
            </Title>
            <p className="aim-login-subtitle">
              Sign in to your account
            </p>
            
            <div style={{ marginBottom: 16 }}>
              <Radio.Group 
                value={serverType} 
                onChange={handleServerChange}
                buttonStyle="solid"
                size="small"
                className="aim-server-switch"
              >
                <Radio.Button value="demo">
                  <Icon type="cloud" /> Demo
                </Radio.Button>
                <Radio.Button value="production">
                  <Icon type="database" /> Production
                </Radio.Button>
              </Radio.Group>
              <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                <Tag color={serverType === 'production' ? 'red' : 'blue'}>
                  {getServerDomain()}
                </Tag>
              </div>
            </div>
          </div>

          <Form
            layout="vertical"
            size="large"
            onSubmit={(e) => {
              e.preventDefault();
              console.log('=== FORM onSubmit TRIGGERED ===');
              
              form.validateFields((err, values) => {
                if (err) {
                  console.log('=== FORM VALIDATION FAILED ===');
                  console.log('Validation errors:', err);
                  message.error('Please fill in all required fields correctly.');
                  return;
                }
                
                console.log('=== FORM VALIDATION PASSED ===');
                console.log('Values received:', values);
                performLoginCall(values);
              });
            }}
          >
            <Form.Item label="Email">
              {form.getFieldDecorator('email', {
                rules: [
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ],
              })(
                <Input
                  prefix={<Icon type="user" style={{ color: '#bfbfbf' }} />}
                  placeholder="Enter your email"
                  autoComplete="email"
                />
              )}
            </Form.Item>

            <Form.Item label="Password">
              {form.getFieldDecorator('password', {
                rules: [
                  { required: true, message: 'Please enter your password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                ],
              })(
                <Input
                  type="password"
                  prefix={<Icon type="lock" style={{ color: '#bfbfbf' }} />}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              )}
            </Form.Item>

            <Form.Item label="Organisation">
              {form.getFieldDecorator('organisation', {
                initialValue: undefined,
                rules: [
                  { required: true, message: 'Please enter or select your organisation' },
                ],
              })(
                <AutoComplete
                  dataSource={savedOrganisations.map((org) => `${org.name} (${org.slug})`)}
                  allowClear
                  filterOption={(inputValue, option) => {
                    if (!inputValue) return true;
                    const searchText = String(inputValue).toLowerCase();
                    const optionText = String(option.props.children || '').toLowerCase();
                    // Extract slug from "Name (slug)" format for searching
                    const slugMatch = optionText.match(/\(([^)]+)\)/);
                    const slug = slugMatch ? slugMatch[1].toLowerCase() : '';
                    return optionText.includes(searchText) || slug.includes(searchText);
                  }}
                  onSelect={(value) => {
                    // Extract slug from "Name (slug)" format or use value directly
                    const match = String(value).match(/\(([^)]+)\)/);
                    const slug = match && match[1] ? match[1] : value;
                    form.setFieldsValue({ organisation: slug });
                  }}
                >
                  <Input
                    prefix={<Icon type="bank" style={{ color: '#bfbfbf' }} />}
                    placeholder="Type organisation slug or select from saved"
                    autoComplete="off"
                    style={{ width: '100%' }}
                  />
                </AutoComplete>
              )}
              {savedOrganisations.length > 0 && (
                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                  {savedOrganisations.length} saved organisation{savedOrganisations.length !== 1 ? 's' : ''} available - type to search or enter new slug
                </div>
              )}
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                className="aim-login-submit"
                style={{ marginTop: 8 }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button 
              type="dashed" 
              size="small"
              onClick={handleTestClick}
              loading={loading}
              style={{ fontSize: 12 }}
            >
              <Icon type="experiment" /> Test Mileage Report API
            </Button>
          </div>

          {errorMessage && (
            <Alert
              message="Login Failed"
              description={errorMessage}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
              closable
              onClose={() => setErrorMessage(null)}
            />
          )}

          {debugInfo && (
            <Collapse className="aim-debug-panel" defaultActiveKey={['debug']}>
              <Panel header="API Response / Debug Information" key="debug">
                <div style={{ marginBottom: 8 }}>
                  <strong>Status:</strong> {debugInfo.status} {debugInfo.success !== undefined && (debugInfo.success ? '✅' : '❌')}
                </div>
                {debugInfo.url && (
                  <div style={{ marginBottom: 8 }}>
                    <strong>URL:</strong> {debugInfo.url}
                  </div>
                )}
                {debugInfo.responseSize && (
                  <div style={{ marginBottom: 8 }}>
                    <strong>Response Size:</strong> {debugInfo.responseSize} bytes
                  </div>
                )}
                <pre className="aim-debug-pre">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </Panel>
            </Collapse>
          )}

          <div style={{ 
            marginTop: 24, 
            textAlign: 'center', 
            color: '#8c8c8c',
            fontSize: 12 
          }}>
            <p style={{ margin: 0 }}>
              By signing in, you agree to our Terms of Service
            </p>
          </div>
        </Card>
      </Content>
    </Layout>
  );
};

export default Form.create({ name: 'login_form' })(LoginPage);
